// The Runqo Console vocabulary: every status, warning, reason and level, with the
// words, icon and tone the UI uses for it. Screens read labels from here so the
// same thing is never called two names.

export type Tone = 'neutral' | 'muted' | 'info' | 'positive' | 'caution' | 'critical';
export interface Term { label: string; short?: string; tone: Tone; icon: string; meaning: string }

// console: shared by Live and Any runner.
const SURVEY_OFFER = 'Offered to signed-in runners as the app asks, spaced out per runner.';

export const STATUS: Record<string, Record<string, Term>> = {
  candidate: {
    pending: { label: 'Pending', tone: 'neutral', icon: 'circle-dashed', meaning: 'Waiting for a decision.' },
    published: { label: 'Published', tone: 'positive', icon: 'circle-check', meaning: 'Became a live event under the curator organization.' },
    linked: { label: 'Linked', tone: 'info', icon: 'link', meaning: 'Attached to an existing published event as the same race. No new event was created.' },
    rejected: { label: 'Rejected', tone: 'muted', icon: 'circle-x', meaning: 'Turned down, with a reason. Can be restored to Pending.' },
    expired: { label: 'Expired', tone: 'muted', icon: 'calendar-x', meaning: 'Its date passed while pending. Hidden, not deleted; returns if the source republishes it with a new date.' },
  },
  run: {
    running: { label: 'Running', tone: 'info', icon: 'loader-circle', meaning: 'Collecting pages now. Only one run per source at a time.' },
    succeeded: { label: 'Succeeded', tone: 'positive', icon: 'circle-check', meaning: 'Every page was fetched and processed.' },
    'partially-failed': { label: 'Partially failed', short: 'Partial', tone: 'caution', icon: 'triangle-alert', meaning: 'Finished, but some pages failed. The failures are listed per page.' },
    failed: { label: 'Failed', tone: 'critical', icon: 'octagon-x', meaning: 'The run could not complete.' },
    halted: { label: 'Halted by spend ceiling', short: 'Halted', tone: 'critical', icon: 'octagon-pause', meaning: 'Stopped because AI spend reached the ceiling. Shown beside the status: a halted run may also have partly failed.' },
    // console: a pause stops a run in progress before its next page, like the ceiling does.
    stopped: { label: 'Stopped by pause', short: 'Stopped', tone: 'caution', icon: 'pause', meaning: 'Stopped before its next page because its source or all automation was paused. Shown beside the status.' },
  },
  organization: {
    pending: { label: 'Pending approval', short: 'Pending', tone: 'caution', icon: 'clock', meaning: 'Waiting for a Runqo operator. It cannot manage events yet.' },
    approved: { label: 'Approved', tone: 'positive', icon: 'badge-check', meaning: 'Can manage its events.' },
    rejected: { label: 'Rejected', tone: 'critical', icon: 'circle-x', meaning: 'Not approved. It cannot manage events.' },
    suspended: { label: 'Suspended', tone: 'critical', icon: 'circle-pause', meaning: 'Temporarily blocked by an operator. Its events stay live but cannot be edited.' },
  },
  source: {
    enabled: { label: 'Enabled', tone: 'positive', icon: 'power', meaning: 'Runs on its schedule and by hand.' },
    paused: { label: 'Paused', tone: 'caution', icon: 'pause', meaning: 'Temporarily stopped by an operator. Resume to run again.' },
    disabled: { label: 'Disabled', tone: 'muted', icon: 'power-off', meaning: 'Retired. Sources are never deleted; history stays.' },
  },
  event: {
    live: { label: 'Live', tone: 'positive', icon: 'circle-check', meaning: 'Visible to runners in the app.' },
    unpublished: { label: 'Unpublished', tone: 'muted', icon: 'eye-off', meaning: 'Taken out of the app. Can be published again.' },
    past: { label: 'Past', tone: 'muted', icon: 'calendar-check', meaning: 'Its date has passed.' },
    // console: an organizer or operator marked it cancelled; it stays in the app with a badge.
    cancelled: { label: 'Cancelled', tone: 'critical', icon: 'calendar-x', meaning: 'Marked cancelled. It stays in the app with a Cancelled badge, and runners who marked interest are told.' },
  },
  // console: the schedule is automation too, and the only kind until trust rules exist.
  automation: {
    on: { label: 'Automation on', tone: 'positive', icon: 'play', meaning: 'Scheduled runs start on their own, and trust rules set to Act act on their own.' },
    paused: { label: 'Automation paused', tone: 'caution', icon: 'pause', meaning: 'Nothing starts or acts on its own: no scheduled run, no rule. Run now and suggestions still work; people decide.' },
  },
  // console: in-app surveys. On/off is the operator's switch; the dates decide the rest.
  survey: {
    live: { label: 'Live', tone: 'positive', icon: 'circle-check', meaning: SURVEY_OFFER },
    scheduled: { label: 'Scheduled', tone: 'info', icon: 'clock', meaning: 'Turned on. The app starts offering it on its first day.' },
    off: { label: 'Off', tone: 'neutral', icon: 'pause', meaning: 'Turned off. The app doesn’t offer it until it’s turned on.' },
    ended: { label: 'Ended', tone: 'muted', icon: 'calendar-check', meaning: 'Its last day has passed. The app no longer offers it; its responses stay.' },
    deleted: { label: 'Deleted', tone: 'muted', icon: 'trash-2', meaning: 'Kept because runners saw or answered it. The app never offers it again.' },
  },
};

// console: who a survey is for, and the kinds of answer the runner app can show.
export const SURVEY_TYPES: Record<string, { label: string; meaning: string }> = {
  periodic: { label: 'Any runner', meaning: SURVEY_OFFER },
  event: {
    label: 'Interested in an event',
    meaning: 'Runners who marked interest in the event, from the day after it for {days} days. Nobody gets it if the event is cancelled. One close ends it for that runner.',
  },
};

export const ANSWER_TYPES: Record<string, { label: string; meaning: string }> = {
  'single-choice': { label: 'Single choice', meaning: 'The runner picks one option.' },
  'multiple-choice': { label: 'Multiple choice', meaning: 'The runner picks one or more options.' },
  // console: the API's Rating, a scale from 1 to 5.
  rating: { label: 'Rating', meaning: 'The runner taps a score from 1 to 5.' },
  'free-text': { label: 'Free text', meaning: 'The runner writes an answer, up to 2,000 characters.' },
};

export interface WarningTerm { label: string; short?: string; icon: string; blocking?: boolean; meaning: string }
export const WARNINGS: Record<string, WarningTerm> = {
  'low-confidence': { label: 'Low confidence', icon: 'gauge', meaning: "The AI's confidence is below the trust bar." },
  'no-start-time': { label: 'No start time', icon: 'clock', meaning: 'Only the date is known. Shown to runners as “Start time to be announced”.' },
  // console: the API also flags dates the pipeline inferred.
  'date-inferred': { label: 'Date inferred', icon: 'calendar', meaning: "The source doesn't state the date exactly, so the pipeline inferred it. Check it against the source." },
  'no-races': { label: 'No races found', icon: 'list-x', meaning: 'The event was found but no races were extracted.' },
  'incomplete-race': { label: 'Incomplete race', icon: 'ruler', meaning: 'A race has no distance yet. It is held back when the event is published and follows when the source fills it in.' },
  'outside-region': { label: 'Outside the region', short: 'Outside region', icon: 'map-pin-off', meaning: 'The location falls outside the Balkans.' },
  'missing-city': { label: 'Missing city', icon: 'map-pin', blocking: true, meaning: 'Publishing is blocked until someone fills in the city.' },
  'source-changed': { label: 'Source changed since your edit', short: 'Source changed', icon: 'file-diff', meaning: 'The source page changed after an operator edited this record. The edits were kept.' },
  // console: the API returns a published event to review when its source page changes.
  'changed-published': { label: 'Published event changed at its source', short: 'Changed since publishing', icon: 'file-diff', meaning: 'This event is live. Its source page changed after it was published, and the app keeps the published version until someone applies the changes.' },
  // console: names what a person can do about it.
  'possibly-cancelled': { label: 'Possibly cancelled', icon: 'calendar-off', meaning: 'The event has been missing from its source for several runs. Nothing was deleted. A person decides: Mark cancelled, Remove from the app or Still on.' },
  // console: what a page announces, read by extraction.
  'cancelled-at-source': { label: 'Cancelled at its source', icon: 'calendar-x', meaning: "The source's page says the event is cancelled. Nothing changes in the app until a person decides." },
  'postponed-at-source': { label: 'Postponed at its source', icon: 'calendar-days', meaning: "The source's page says the event is postponed. Nothing changes in the app until a person decides." },
  'duplicate-suspected': { label: 'Duplicate suspected', short: 'Duplicate?', icon: 'copy', meaning: 'This may be the same race as another record. Opens its duplicate group.' },
};

// Why the machine stopped and handed a record to a person. The inbox groups by these.
export const REASONS: Record<string, { label: string; icon: string; tone: Tone; meaning: string }> = {
  'low-confidence': { label: 'Confidence below the trust bar', icon: 'gauge', tone: 'caution', meaning: "The AI's confidence in what it extracted is below the trust bar, so a person checks it against the source." },
  'blocking-warning': { label: 'A warning blocks publishing', icon: 'octagon-alert', tone: 'critical', meaning: 'A warning such as Missing city blocks publishing until someone fills in what is missing.' },
  'duplicate-group': { label: 'Duplicate group needs a winner', icon: 'copy', tone: 'caution', meaning: 'Several records look like the same race, and a person picks the one to keep.' },
  'changed-published': { label: 'Published event changed at its source', icon: 'file-diff', tone: 'caution', meaning: 'A live event changed at its source, and a person decides whether to apply the changes, keep the current version or remove it from the app.' },
  // console: names what a person can do about it.
  'possibly-cancelled': { label: 'Possibly cancelled', icon: 'calendar-off', tone: 'caution', meaning: 'The event has been missing from its source for several runs, and a person decides: Mark cancelled, Remove from the app or Still on.' },
  // console: what a source page announces, read by extraction.
  'cancelled-at-source': { label: 'Cancelled at its source', icon: 'calendar-x', tone: 'caution', meaning: "The source's page says the event is cancelled, and nothing changes in the app until a person decides: on a live event, Apply changes or Keep current version; on a new record, Publish it as cancelled or Reject it." },
  // console: what a source page announces, read by extraction.
  'postponed-at-source': { label: 'Postponed at its source', icon: 'calendar-days', tone: 'caution', meaning: "The source's page says the event is postponed, and nothing changes in the app until a person decides: on a live event, Apply changes (it takes a new date only if the page gives one) or Keep current version; on a new record, Publish it or Reject it." },
  'source-changed': { label: 'Source changed since your edit', icon: 'file-diff', tone: 'caution', meaning: 'The source page changed after a person edited this record; the edits were kept, and someone checks they still hold.' },
  'suggestion': { label: 'Pipeline suggests an action', icon: 'bot', tone: 'info', meaning: 'A trust rule set to Suggest proposes a decision, and a person confirms it with one key or decides otherwise.' },
  'no-rule': { label: 'No trust rule covers this', icon: 'shield-check', tone: 'neutral', meaning: 'No trust rule is set to Suggest or Act for records like this, so a person decides.' },
  'source-failing': { label: 'Source is failing', icon: 'octagon-x', tone: 'critical', meaning: "Recent runs of this source failed, so its events aren't being kept current." },
  'source-silent': { label: 'Source returned nothing', icon: 'circle-off', tone: 'critical', meaning: 'The last run fetched pages but found no events; the site may have changed.' },
  // console: a coverage gap only a redeploy clears.
  'source-capped': { label: 'Source is capped', icon: 'layers', tone: 'caution', meaning: "The last run stopped at the most event pages a run reads, so pages past that point weren't read and events that vanish from the listing aren't flagged as possibly cancelled. Raise Scraping:MaxEventsPerRun in the API's configuration and redeploy; it clears after the next run that isn't capped." },
  // console: a scheduled run is missing.
  'source-overdue': { label: 'Nothing ran when due', icon: 'clock-alert', tone: 'critical', meaning: 'A scheduled run should have started and none did, so its events are going stale.' },
  // console: yield or quality is falling, short of failing.
  'source-slipping': { label: 'Source is slipping', icon: 'trending-down', tone: 'caution', meaning: 'Yield or extraction quality is going down.' },
  // console: the AI provider didn't accept calls (its account or key), which only a person fixes.
  'ai-account-refused': { label: "AI provider didn't accept calls", icon: 'ban', tone: 'critical', meaning: "The provider in use didn't accept the last run's calls, for example because its account has no credit or its key is missing, so the pages they covered were not read until a person fixes it or changes the AI model." },
  'spend-alert': { label: 'Spend passed the alert threshold', icon: 'coins', tone: 'caution', meaning: 'AI spend passed the alert threshold; at the ceiling, new runs are refused and running ones stopped.' },
  // console: AI calls that recorded no cost leave the ceiling blind, which needs a person.
  'spend-unrecorded': { label: "Spend isn't being recorded", icon: 'coins', tone: 'caution', meaning: "AI calls recorded no cost, so neither the alert threshold nor the ceiling can act, and a person checks that pricing is set." },
};

export const HEALTH: Record<string, Term> = {
  healthy: { label: 'Healthy', tone: 'positive', icon: 'circle-check', meaning: 'Last run succeeded; yield and quality are steady.' },
  slipping: { label: 'Slipping', tone: 'caution', icon: 'trending-down', meaning: 'Yield or extraction quality is going down.' },
  failing: { label: 'Failing', tone: 'critical', icon: 'octagon-x', meaning: 'Recent runs failed.' },
  silent: { label: 'Returned nothing', tone: 'critical', icon: 'circle-off', meaning: 'The last run fetched pages but found no events. The site may have changed.' },
  // console: the last run stopped at the event-page cap.
  capped: { label: 'Capped', tone: 'caution', icon: 'layers', meaning: "The last run stopped at the most event pages a run reads, so pages past that point weren't read and events that vanish from the listing aren't flagged as possibly cancelled. Raise Scraping:MaxEventsPerRun in the API's configuration and redeploy; it clears after the next run that isn't capped." },
  paused: { label: 'Paused', tone: 'caution', icon: 'pause', meaning: 'An operator paused this source.' },
  // console: judged from the schedule, not from a run.
  overdue: { label: 'Overdue', tone: 'critical', icon: 'clock-alert', meaning: 'A scheduled run should have started and none did.' },
  // console: the last run fetched nothing and the scheduler is still trying.
  retrying: { label: 'Retrying', tone: 'caution', icon: 'refresh-cw', meaning: 'The last run fetched nothing; it is tried again after an hour, then four.' },
  'not-run': { label: 'Not run yet', tone: 'neutral', icon: 'circle-dashed', meaning: 'Added but never run.' },
};

export const MATCH: Record<string, { label: string; pips: number; meaning: string }> = {
  likely: { label: 'Likely', pips: 3, meaning: 'Strong evidence this is the same race.' },
  possible: { label: 'Possible', pips: 2, meaning: 'Some evidence; check before linking.' },
  unlikely: { label: 'Unlikely', pips: 1, meaning: 'Little evidence; probably different races.' },
};

export const TRUST: Record<string, { label: string; meaning: string }> = {
  off: { label: 'Off', meaning: 'People decide every record. Today’s manual mode.' },
  suggest: { label: 'Suggest', meaning: 'The pipeline proposes; a person confirms with one key.' },
  act: { label: 'Act', meaning: 'The pipeline acts on its own and records why. Anyone can undo.' },
};

// console: the two trust-rule actions that decide an open duplicate group. `suggested` follows "Suggested:".
export const GROUP_ACTIONS: Record<'DismissUnlikely' | 'RejectOwnerDuplicate', { title: string; meaning: string; suggested: string }> = {
  DismissUnlikely: { title: 'Dismiss unlikely groups', meaning: 'Marks a group as different events when nothing in it is alike.', suggested: 'Different events' },
  RejectOwnerDuplicate: { title: "Keep organizers' own events", meaning: 'Rejects the candidates that duplicate an event an organizer published.', suggested: "Keep the organizer's event" },
};

// console: how the audit trail and a rule's preview read a group's resolution, by the API's resolution. `unnamed` stands in when no winner has a name.
export const GROUP_RESOLUTIONS = {
  NotDuplicates: { trail: 'Different events' },
  KeepExistingEvent: { trail: 'Kept the published event' },
  KeepStaged: { trail: 'Kept', unnamed: 'a candidate' },
};

// console: why a group rule leaves an open group to people, by the API's reason.
export const GROUP_SKIPS: Record<string, string> = {
  NotOpen: 'The group is resolved',
  TooFewItems: 'Only one record or event is left in it',
  EditedByPerson: 'A person edited a record in it',
  RenamedEvent: 'A person named the event it suspects or links to',
  NotUnlikely: 'Two of its items look alike (Possible or better)',
  SharedName: 'Two names share a word',
  BelowBar: "A record is below the rule's confidence",
  MixedRules: "Its records' sources follow different rules",
  NotOrganizers: "It suspects no organizer's own event",
  NotAllPending: 'A record in it is already decided',
  LinkedByPersonOrRule: 'A person or a rule linked a record in it',
  MatchNotConfirmed: "A record isn't a confirmed match with the event",
  RuleDecisionUndone: "A person undid this rule's decision on it",
};

// console: the line under a pending record that is linked to an event. The name of whoever linked it follows "linked by".
export const LINK_LINE = 'Publishing joins';

// console: the Inbox region's heading, shared by its rows and a run's rule figures.
const RULE_HEADING = 'What rules did';

// console: what a run's page says between the run finishing and its rules having decided.
export const RULES_PENDING = 'Rules are deciding on what this run found.';

// console: a retry run's trigger reads "Scheduled, retry ⟨n⟩ of ⟨of⟩".
export const TRIGGER_RETRY = 'retry';
export const TRIGGER_RETRY_OF = 'of';

// console: the Inbox's "What rules did" region. The row reads "Rules decided N" plus the day in words; a first visit counts the last 7 days.
export const RULE_ACTIVITY = {
  region: RULE_HEADING,
  decided: 'Rules decided',
  today: 'today',
  yesterday: 'since yesterday',
  since: 'since',
  lastWeek: 'in the last 7 days',
};

// console: a rule's figures on the Inbox's rows and on a run or import: “Rule” decided D [on source] and left L for the next run.
export const RULE_FIGURES = {
  heading: RULE_HEADING,
  decided: 'decided',
  on: 'on',
  and: 'and',
  left: 'left',
  forNextRun: 'for the next run',
};

// console: what the review page's decision button says, by what approving does to the record. `linkElsewhere` is L on a record that is already linked, so the two link buttons differ.
export const DECISIONS = {
  publish: 'Publish',
  link: 'Link',
  apply: 'Apply changes',
  linkElsewhere: 'Link to another event…',
};
// console: Unlink on a pending record's link line. The trail's Unlink (undoing a Linked publish) is worded in the audit vocab.
export const UNLINK = {
  action: 'Unlink',
  from: 'Unlink from',
  done: 'Unlinked from',
  consequence: 'Publishing now creates a new event.',
  relinked: 'Relinked',
  refused: "Couldn't unlink",
  undoRefused: "Couldn't undo the change to",
  gone: 'an event no longer in the app',
};
// console: what the review page says while an open edit or typed search holds the keys.
export const KEYS_HOLD = {
  race: 'Keys are paused until you save or cancel',
  search: 'Keys are paused until you clear the search',
  help: 'Every other key pauses while a race edit or a link search is open',
};
// console: the review page's catalog search, which looks near the record's date first. Sentences are built as "No event within 14 days of 20 October matches “name”".
export const LINK_SEARCH = {
  hint: 'By name or city',
  renamed: "Renamed events won't show among the matches above.",
  within: 'within',
  daysOf: 'days of',
  everyUpcoming: 'among every upcoming date',
  noEvent: 'No event',
  noUpcoming: 'No upcoming event',
  matches: 'matches',
  tryName: 'Try part of the name, or the city.',
  searchEvery: 'Search every upcoming date',
  back: 'Back to dates near the record',
  showingFirst: 'Showing the first',
  of: 'of',
  narrow: 'Narrow the search to see the rest.',
  orderCovers: 'The order covers only the first',
  narrowNearest: 'narrow the search to be sure of the nearest.',
};
// console: the review page's pin row: where the app will put the event on a map.
export const PIN = {
  label: 'Pin on the map',
  open: 'Open in maps',
  none: 'None: the app shows no map',
};
export const LINK_ORIGINS: Record<string, { label: string; meaning: string }> = {
  pipeline: { label: 'linked by the pipeline (Likely match)', meaning: 'The dedup pass linked it because the match is Likely, and judges it again when the record changes.' },
  person: { label: 'linked by', meaning: 'A person linked it or approved it. The pipeline leaves it alone. Followed by their name.' },
  rule: { label: 'linked by the rule', meaning: 'A trust rule linked it or approved it. The pipeline leaves it alone. Followed by the rule’s name in quotes.' },
  unrecorded: { label: "who linked it isn't recorded", meaning: 'Nothing says who made the link: it predates the record of origins, or an undo rolled it back to one.' },
};

export const TRIGGERS: Record<string, { label: string; icon: string }> = {
  manual: { label: 'Manual', icon: 'hand' },
  scheduled: { label: 'Scheduled', icon: 'timer' },
  import: { label: 'Import', icon: 'upload' },
};

// console: the API's organization types. The curator is Runqo's own; an owner organization is an organizer's.
export const ORGANIZATION_TYPES: Record<string, { label: string; meaning: string }> = {
  curator: { label: 'Curator', meaning: 'Runqo’s own organization. Published candidates become its events.' },
  owner: { label: 'Owner', meaning: 'An organizer’s own organization. Its members manage its events.' },
};

// console: a member's role in an organization. Only the owner changes who belongs to it.
export const MEMBER_ROLES: Record<string, { label: string; meaning: string }> = {
  owner: { label: 'Owner', meaning: 'Adds and removes members, moves events to other organizations and can delete the organization.' },
  member: { label: 'Member', meaning: 'Manages the organization’s events and sees the runners interested in them.' },
};

export const CATEGORIES = ['5K', '10K', 'Half Marathon', 'Marathon', 'Ultramarathon', 'Trail', 'Fun Run', 'OCR', 'Other'];

// console: the id of a blocked decision's reason, which the disabled buttons are described by.
export const DECIDE_WHY_ID = 'rq-decide-why';
